import {
  Button,
  Card,
  CardActions,
  CardContent,
  colors,
  Grid,
  Modal,
  TextField,
  Theme,
  Typography
} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import clsx from 'clsx';
import React, {useEffect, useState} from 'react';
import {T} from 'react-polyglot-hooks';
import availableHotels from './getAvailableHotels.api';
import {Resource} from '../../../graphql/resources.datatypes';
import moment, {Moment} from 'moment';
import anyLoading from '../../../utils/anyLoading';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import {formatDate} from '../../../utils/formatters';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 700,
    maxHeight: '90%',
    overflowY: 'auto',
    maxWidth: '95%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  deleteButton: {
    color: theme.palette.white,
    backgroundColor: colors.red[600],
    '&:hover': {
      backgroundColor: colors.red[900]
    }
  },
  submitButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  }
}));

export const checkRoomAvailable = (hotel: Resource, start: Moment, end: Moment, capacity?: number) => {
  const dayCount = end.diff(start, 'days');
  const passed = hotel.roomResources && hotel.roomResources
      .filter(roomResource => (capacity !== undefined && capacity > 0) ? roomResource.guest_capacity! >= capacity : true)
      .map(roomResource => {
        if (!roomResource.resource_banks) return false;
        const dayRange = roomResource.resource_banks && roomResource.resource_banks
            .filter(resourceBank => {
              const date = moment(new Date(resourceBank.date!));
              return date.isSameOrAfter(start, 'days') && date.isBefore(end, 'days') && resourceBank.remaining! > 0;
            });
        return roomResource.allow_manual_overbooking || (dayRange && dayRange.length === dayCount);
      })
      .find(pass => pass === true);
  return passed === true;
};

const AvailableHotelModal = (props: {
  className?: string,
  onClose: () => void,
  onConfirmClick: (hotel?: Resource) => void,
  open: boolean,
  tourResourceId: number,
  startDate: Moment
  endDate: Moment,
  guestCapacity?: number,
  currentHotelId: number,
  isRoomParty: boolean,
}) => {
  const {open, onClose, onConfirmClick, tourResourceId, startDate, endDate, guestCapacity, currentHotelId, isRoomParty, ...rest} = props;
  const classes = useStyles();
  const hotelsData = availableHotels(props.tourResourceId);
  const loadingData = anyLoading(hotelsData)
  const [selectedHotelId, setSelectedHotelId] = useState<number>(currentHotelId);
  const [roomAvailable, setRoomAvailable] = useState(false);

  const getSelectedHotel = () => hotelsData.data!.availableHotels.find(hotel => hotel.id === selectedHotelId);

  useEffect(() => {
    if (hotelsData.data && hotelsData.data.availableHotels) {
        const selectedHotel = getSelectedHotel();
        if (selectedHotel) {
          const available = checkRoomAvailable(selectedHotel, startDate, endDate, guestCapacity);
          setRoomAvailable(available);
        }
    }
  }, [selectedHotelId, hotelsData.data])

  if (!open) {
    return null;
  }

  return (
      <Modal onClose={onClose} open={open}>
        <Card {...rest} className={clsx(classes.root)}>
          <form>
            <LoadingOverlay loading={loadingData.loading}>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography gutterBottom variant="h3">Select Available Hotel</Typography>

                    {currentHotelId !== -1 && !isRoomParty && (
                        <Typography variant="body1" color={'error'}>
                          Warning! You are about to change the hotel for a group. Please be sure about this update as it
                          could cause problems and cannot be undone. Make sure there is enough capacity in the room bank
                          for all of the rooms being transferred and that any options on the rooms being transferred are
                          known.<br/><br/>
                        </Typography>
                    )}

                    {currentHotelId !== -1 && isRoomParty && (
                        <Typography variant="body1" color={'error'}>
                          Warning! You are about to change the hotel for this room party. Please be sure about this update as it
                          could cause problems and cannot be undone. Make sure there is enough capacity in the room bank
                          for this room and that any options on the room being transferred are known.<br/><br/>
                        </Typography>
                    )}

                    {currentHotelId === -1 && (
                        <Typography variant="body1" color={'error'}>
                          Please remember to set the Group dates before setting the hotel.<br/><br/>
                        </Typography>
                    )}

                    {loadingData.loading && (
                        <span>please wait...</span>
                    )}
                    {!loadingData.loading && (<TextField
                            fullWidth
                            label="Hotel"
                            name="hotel_resource_id"
                            onChange={(event) => setSelectedHotelId(Number(event.target.value))}
                            select
                            // eslint-disable-next-line react/jsx-sort-props
                            SelectProps={{native: true}}
                            value={selectedHotelId}
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                            }}
                        >
                          <option value={-1}>Please select...</option>
                          {hotelsData.data && hotelsData.data.availableHotels && hotelsData.data.availableHotels.map((hotel) => (
                              <option key={hotel.id} value={hotel.id}>
                                {hotel.internal_name}
                              </option>
                          ))}
                        </TextField>
                    )}

                  </Grid>
                  <Grid item xs={12}>
                    {guestCapacity !== undefined && (
                        <React.Fragment>
                          <Typography gutterBottom variant="body1">
                            Guest Capacity: {guestCapacity}
                          </Typography>
                          <Typography gutterBottom variant="body1">
                            Date Range: {formatDate(startDate)} to {formatDate(endDate)}
                          </Typography>
                          <Typography gutterBottom variant="body1">
                            Room available? {roomAvailable ? 'yes' : 'no'}
                          </Typography>
                        </React.Fragment>
                    )}
                  </Grid>
                </Grid>
              </CardContent>


              <CardActions className={classes.actions}>
                <Button onClick={() => {
                  onClose();
                }} variant="contained">
                  <T phrase="Cancel"/>
                </Button>
                <Button
                    className={classes.submitButton}
                    onClick={() => {
                      onClose();
                      onConfirmClick(getSelectedHotel());
                    }}
                    variant="contained"
                    disabled={!(roomAvailable && (selectedHotelId !== -1))}
                >
                  <T phrase="Confirm"/>
                </Button>
              </CardActions>
            </LoadingOverlay>
          </form>
        </Card>
      </Modal>
  );
};

export default AvailableHotelModal;
